import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Link from 'flarum/common/components/Link';
import { brandLine, sponsorCard, signupBox, RESIDENCE_COUNTRY } from '../sponsorHooks';

// ============================================================
// Tax rates — Nigeria Tax Act 2025. Kept in this component (not
// DB-driven like the Income Tax Calculator's brackets) since this
// calculator's rates weren't asked to be admin-editable; revisit
// if that changes.
// ============================================================
const TAX_RATES = {
  citLargeRate: 0.30,
  developmentLevy: 0.04,
  capitalGainsTaxCompany: 0.30,
  dividendWHT: 0.10,
  interestWHT: 0.10,
  rentalWHT: 0.10,
  PAYE: [{
    min: 0,
    max: 800000,
    rate: 0
  }, {
    min: 800000,
    max: 3000000,
    rate: 0.15
  }, {
    min: 3000000,
    max: 12000000,
    rate: 0.18
  }, {
    min: 12000000,
    max: 25000000,
    rate: 0.21
  }, {
    min: 25000000,
    max: 50000000,
    rate: 0.23
  }, {
    min: 50000000,
    max: Infinity,
    rate: 0.25
  }]
};
const TAX_WARNINGS = {
  canada: {
    title: '🇨🇦 Canada Tax Resident with Nigerian Business',
    html: `
      <p><strong>Critical Tax Implications:</strong></p>
      <ul>
        <li><strong>Worldwide Income Taxation:</strong> As a Canadian tax resident, you must report ALL worldwide income to the CRA, including profits from your Nigerian business.</li>
        <li><strong>Double Taxation Risk:</strong> You'll pay CIT in Nigeria (0-30%) AND Canadian corporate/personal tax (26.5%-53.5% combined) on the same income.</li>
        <li><strong>Tax Treaty Relief:</strong> Canada-Nigeria Tax Treaty allows foreign tax credits — you can claim Nigerian taxes paid against Canadian taxes owed.</li>
        <li><strong>Capital from Canada:</strong> Transfers over CAD $10,000 may face FINTRAC reporting scrutiny.</li>
        <li><strong>Repatriation:</strong> Dividends from Nigeria to Canada face 10% WHT in Nigeria plus Canadian dividend tax.</li>
      </ul>
      <p><strong>Recommended:</strong> consult a cross-border tax accountant, file T1135 if foreign property exceeds CAD $100,000, and keep detailed records of Nigerian taxes paid.</p>
    `
  },
  us: {
    title: '🇺🇸 US Tax Resident with Nigerian Business',
    html: `
      <p><strong>Critical Tax Implications:</strong></p>
      <ul>
        <li><strong>Worldwide Income Taxation:</strong> US citizens and residents must report ALL worldwide income to the IRS.</li>
        <li><strong>Double Taxation:</strong> Nigerian CIT (0-30%) plus US corporate tax (21% federal + state) or personal tax (10-37%) on the same income.</li>
        <li><strong>FBAR Requirement:</strong> If Nigerian business accounts exceed $10,000 USD at any point, you must file FinCEN Form 114.</li>
        <li><strong>Form 5471:</strong> Required annually for US persons with 10%+ ownership in a foreign corporation.</li>
        <li><strong>GILTI Tax:</strong> Controlled Foreign Corporation income may face Global Intangible Low-Taxed Income tax.</li>
      </ul>
      <p><strong>Recommended:</strong> hire a US international tax CPA, file Form 8938 if foreign assets exceed $50,000, maintain separate Nigerian GAAP vs US GAAP books.</p>
    `
  },
  uk: {
    title: '🇬🇧 UK Tax Resident with Nigerian Business',
    html: `
      <p><strong>Critical Tax Implications:</strong></p>
      <ul>
        <li><strong>Worldwide Income Taxation:</strong> UK tax residents pay UK tax on worldwide income, including Nigerian business profits.</li>
        <li><strong>Double Taxation:</strong> Nigerian CIT (0-30%) plus UK Corporation Tax (25%) or Income Tax (20-45%) on the same income.</li>
        <li><strong>CFC Rules:</strong> May apply if the Nigerian company is 50%+ UK-owned and profits exceed £500,000.</li>
        <li><strong>Transfer Pricing:</strong> HMRC scrutinizes transactions between UK residents and Nigerian companies.</li>
      </ul>
      <p><strong>Recommended:</strong> consult a UK chartered accountant with international tax experience, file SA100 plus SA106 (Foreign Income).</p>
    `
  },
  uae: {
    title: '🇦🇪 UAE Resident with Nigerian Business',
    html: `
      <p><strong>Tax Advantages:</strong></p>
      <ul>
        <li><strong>No Personal Income Tax:</strong> UAE has 0% personal income tax on repatriated dividends.</li>
        <li><strong>Corporate Tax:</strong> 9% UAE corporate tax applies to mainland companies earning over AED 375,000 (free zones may differ).</li>
        <li><strong>No Tax Treaty:</strong> Nigeria and UAE have no double taxation treaty — no tax credit relief available.</li>
      </ul>
      <p><strong>Recommended:</strong> consider a UAE free zone holding entity, optimize dividend timing, maintain a UAE tax residency certificate.</p>
    `
  },
  other: {
    title: '⚠️ International Tax Resident with Nigerian Business',
    html: `
      <p><strong>General Considerations:</strong></p>
      <ul>
        <li>Most countries tax worldwide income — you'll likely pay tax in both your country of residence AND Nigeria.</li>
        <li>Check if your country has a Double Taxation Agreement (DTA) with Nigeria for relief.</li>
        <li>Keep detailed records of all Nigerian taxes paid for foreign tax credit claims.</li>
        <li>Consult an international tax advisor familiar with both jurisdictions.</li>
      </ul>
    `
  }
};
const FUNDING_WARNINGS = {
  'foreign-personal': `
    <p><strong>Funding from Personal Foreign Funds:</strong></p>
    <ul>
      <li>Nigerian banks may require proof of legitimate source for large transfers (anti-money laundering).</li>
      <li>Capital contributions are NOT taxable income in Nigeria.</li>
      <li>Return of capital can be repatriated tax-free, but profits face dividend WHT.</li>
      <li>Keep clear records separating capital contributions vs loans vs profits.</li>
    </ul>
  `,
  'foreign-investors': `
    <p><strong>Foreign Investor Capital:</strong></p>
    <ul>
      <li>May require Nigerian Investment Promotion Commission (NIPC) registration.</li>
      <li>File a Certificate of Capital Importation (CCI) with CBN for repatriation rights.</li>
      <li>Foreign investors can repatriate dividends after paying 10% WHT (or treaty rate).</li>
    </ul>
  `,
  'foreign-loans': `
    <p><strong>Foreign Bank Loans:</strong></p>
    <ul>
      <li>Interest payments to foreign lenders face 10% WHT in Nigeria (or treaty rate).</li>
      <li>Thin capitalization rules may restrict debt-to-equity ratio.</li>
      <li>Interest is tax-deductible in Nigeria if properly structured.</li>
    </ul>
  `,
  mixed: `
    <p><strong>Mixed Funding Sources:</strong></p>
    <ul>
      <li>Maintain clear separation between Nigerian and foreign funding sources.</li>
      <li>Document each capital infusion separately with proper agreements.</li>
    </ul>
  `
};
const STEP_LABELS = ['Business Profile', 'Income & Revenue', 'Expenses & Deductions', 'Results'];
function formatCurrency(amount) {
  return '₦' + new Intl.NumberFormat('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
}
function calculatePAYE(income) {
  let tax = 0;
  let remaining = income;
  for (const bracket of TAX_RATES.PAYE) {
    if (remaining <= 0) break;
    const taxableInBracket = Math.min(remaining, bracket.max - bracket.min);
    tax += taxableInBracket * bracket.rate;
    remaining -= taxableInBracket;
  }
  return tax;
}
export default class BusinessTaxCalculatorPage extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.step = 1;
    this.countryOfResidence = '';
    this.businessLocation = '';
    this.fundingSource = '';
    this.repatriateIncome = '';
    this.entityType = '';
    this.industry = '';
    this.yearsInOperation = 5;
    this.grossRevenue = 0;
    this.costOfSales = 0;
    this.rentalIncome = 0;
    this.dividendIncome = 0;
    this.interestIncome = 0;
    this.capitalGains = 0;
    this.operatingExpenses = 0;
    this.depreciation = 0;
    this.interestExpense = 0;
    this.badDebts = 0;
    this.pensionContributions = 0;
    this.donations = 0;
    this.results = null;
    this.error = null;
    this.saving = false;
    this.saveMessage = null;
    this.history = null;
    this.loadingHistory = false;
    if (app.session.user) this.loadHistory();
  }
  validateStep(step) {
    this.error = null;
    if (step === 1) {
      if (!this.countryOfResidence || !this.businessLocation || !this.fundingSource || !this.repatriateIncome || !this.entityType) {
        this.error = 'Please fill in all required fields marked with *.';
        return false;
      }
    }
    if (step === 2) {
      const grossRevenue = parseFloat(this.grossRevenue) || 0;
      if (grossRevenue <= 0) {
        this.error = 'Please enter a valid gross revenue amount.';
        return false;
      }
    }
    return true;
  }
  goToStep(step) {
    if (step > this.step && !this.validateStep(this.step)) return;
    this.error = null;
    this.step = step;
    window.scrollTo({
      top: this.element ? this.element.offsetTop - 20 : 0,
      behavior: 'smooth'
    });
  }
  calculate() {
    if (!this.validateStep(2)) {
      this.step = 2;
      return;
    }
    const grossRevenue = parseFloat(this.grossRevenue) || 0;
    const costOfSales = parseFloat(this.costOfSales) || 0;
    const rentalIncome = parseFloat(this.rentalIncome) || 0;
    const dividendIncome = parseFloat(this.dividendIncome) || 0;
    const interestIncome = parseFloat(this.interestIncome) || 0;
    const capitalGains = parseFloat(this.capitalGains) || 0;
    const operatingExpenses = parseFloat(this.operatingExpenses) || 0;
    const depreciation = parseFloat(this.depreciation) || 0;
    const interestExpense = parseFloat(this.interestExpense) || 0;
    const badDebts = parseFloat(this.badDebts) || 0;
    const pensionContributions = parseFloat(this.pensionContributions) || 0;
    const donations = parseFloat(this.donations) || 0;
    const grossProfit = grossRevenue - costOfSales;
    const totalDeductions = operatingExpenses + depreciation + interestExpense + badDebts + pensionContributions + donations;
    const assessableProfit = Math.max(0, grossProfit - totalDeductions);
    let citRate = 0;
    let citAmount = 0;
    let entityDescription = '';
    if (this.entityType === 'sole-proprietor') {
      citAmount = calculatePAYE(assessableProfit);
      citRate = assessableProfit > 0 ? citAmount / assessableProfit : 0;
      entityDescription = 'Sole Proprietor (Progressive PAYE: 0-25%)';
    } else if (this.entityType === 'small-company') {
      citRate = 0;
      citAmount = 0;
      entityDescription = 'Small Company (≤₦100M turnover & ≤₦250M assets) — Tax Exempt';
    } else {
      citRate = TAX_RATES.citLargeRate;
      citAmount = assessableProfit * citRate;
      entityDescription = 'Large Company (>₦100M turnover or >₦250M assets)';
    }
    const developmentLevyAmount = this.entityType !== 'sole-proprietor' && this.entityType !== 'small-company' ? assessableProfit * TAX_RATES.developmentLevy : 0;
    const dividendWHT = dividendIncome * TAX_RATES.dividendWHT;
    const interestWHT = interestIncome * TAX_RATES.interestWHT;
    const rentalWHT = rentalIncome * TAX_RATES.rentalWHT;
    const totalWHT = dividendWHT + interestWHT + rentalWHT;
    const cgtRate = this.entityType === 'sole-proprietor' ? 0 : TAX_RATES.capitalGainsTaxCompany;
    const cgtAmount = capitalGains * cgtRate;
    const totalTax = citAmount + developmentLevyAmount + totalWHT + cgtAmount;
    this.results = {
      entityType: this.entityType,
      entityDescription,
      grossRevenue,
      assessableProfit,
      citRate,
      citAmount,
      developmentLevyAmount,
      totalWHT,
      cgtAmount,
      totalTax,
      effectiveTaxRate: grossRevenue > 0 ? totalTax / grossRevenue * 100 : 0
    };
    this.step = 4;
    if (app.session.user) this.save();
  }
  async save() {
    if (!this.results) return;
    this.saving = true;
    this.saveMessage = null;
    m.redraw();
    try {
      const response = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/voucha-calculators/business-tax/save',
        body: {
          country_of_residence: this.countryOfResidence,
          business_location: this.businessLocation,
          funding_source: this.fundingSource,
          repatriate_income: this.repatriateIncome,
          entity_type: this.entityType,
          industry: this.industry,
          years_in_operation: parseInt(this.yearsInOperation) || 0,
          gross_revenue: this.results.grossRevenue,
          cost_of_sales: parseFloat(this.costOfSales) || 0,
          rental_income: parseFloat(this.rentalIncome) || 0,
          dividend_income: parseFloat(this.dividendIncome) || 0,
          interest_income: parseFloat(this.interestIncome) || 0,
          capital_gains: parseFloat(this.capitalGains) || 0,
          operating_expenses: parseFloat(this.operatingExpenses) || 0,
          depreciation: parseFloat(this.depreciation) || 0,
          interest_expense: parseFloat(this.interestExpense) || 0,
          bad_debts: parseFloat(this.badDebts) || 0,
          pension_contributions: parseFloat(this.pensionContributions) || 0,
          donations: parseFloat(this.donations) || 0,
          assessable_profit: this.results.assessableProfit,
          cit_amount: this.results.citAmount,
          development_levy_amount: this.results.developmentLevyAmount,
          wht_amount: this.results.totalWHT,
          cgt_amount: this.results.cgtAmount,
          total_tax: this.results.totalTax,
          effective_tax_rate: this.results.effectiveTaxRate
        }
      });
      if (response.success) {
        this.saveMessage = {
          type: 'success',
          text: 'Calculation saved to your account.'
        };
        this.loadHistory();
      } else {
        this.saveMessage = {
          type: 'error',
          text: response.message || 'Could not save.'
        };
      }
    } catch (e) {
      this.saveMessage = {
        type: 'error',
        text: 'Could not save. Please try again.'
      };
    }
    this.saving = false;
    m.redraw();
  }
  async loadHistory() {
    if (!app.session.user) return;
    this.loadingHistory = true;
    m.redraw();
    try {
      const response = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/voucha-calculators/business-tax/history'
      });
      this.history = response.success ? response.data : [];
    } catch (e) {
      this.history = [];
    }
    this.loadingHistory = false;
    m.redraw();
  }
  async clearHistory() {
    if (!window.confirm('Permanently delete all your saved business tax calculations? This cannot be undone.')) {
      return;
    }
    this.loadingHistory = true;
    m.redraw();
    try {
      await app.request({
        method: 'DELETE',
        url: app.forum.attribute('apiUrl') + '/voucha-calculators/business-tax/history'
      });
      this.history = [];
    } catch (e) {
      // leave existing history displayed if the delete failed
    }
    this.loadingHistory = false;
    m.redraw();
  }
  reset() {
    this.step = 1;
    this.countryOfResidence = '';
    this.businessLocation = '';
    this.fundingSource = '';
    this.repatriateIncome = '';
    this.entityType = '';
    this.industry = '';
    this.yearsInOperation = 5;
    this.grossRevenue = 0;
    this.costOfSales = 0;
    this.rentalIncome = 0;
    this.dividendIncome = 0;
    this.interestIncome = 0;
    this.capitalGains = 0;
    this.operatingExpenses = 0;
    this.depreciation = 0;
    this.interestExpense = 0;
    this.badDebts = 0;
    this.pensionContributions = 0;
    this.donations = 0;
    this.results = null;
    this.error = null;
    this.saveMessage = null;
  }
  view() {
    return m("div", {
      className: "VouchaCalculator container"
    }, m("div", {
      className: "VouchaCalculator-header"
    }, m("h1", null, "Business Tax Calculator"), m("p", {
      className: "helpText"
    }, "Calculate Company Income Tax (CIT), Development Levy & more"), m("div", {
      className: "VouchaCalculator-badge"
    }, "Updated: Nigeria Tax Act 2025 (Effective Jan 1, 2026)")), m("div", {
      className: "VouchaCalculator-wizardCard"
    }, m("div", {
      className: "VouchaCalculator-steps"
    }, STEP_LABELS.map((label, i) => {
      const stepNum = i + 1;
      const state = stepNum === this.step ? 'is-active' : stepNum < this.step ? 'is-completed' : '';
      return m("div", {
        className: `VouchaCalculator-step ${state}`
      }, m("div", {
        className: "VouchaCalculator-stepCircle"
      }, stepNum), m("div", {
        className: "VouchaCalculator-stepLabel"
      }, label));
    })), m("div", {
      className: "VouchaCalculator-progressBarTrack"
    }, m("div", {
      className: "VouchaCalculator-progressBarFill",
      style: {
        width: `${this.step / 4 * 100}%`
      }
    })), this.step === 1 && this.renderStep1(), this.step === 2 && this.renderStep2(), this.step === 3 && this.renderStep3(), this.step === 4 && this.results && sponsorCard('business-tax', RESIDENCE_COUNTRY[this.countryOfResidence]), this.step === 4 && this.results && this.renderResults()), m("div", {
      className: "VouchaCalculator-relatedTools"
    }, m("h2", null, "Related tools"), m(Link, {
      href: "/tax-calculator"
    }, "Income Tax Calculator"), m(Link, {
      href: "/salary-converter"
    }, "Salary Converter")), signupBox('calculator-business-tax'), app.session.user && this.renderHistorySection());
  }
  renderStep1() {
    const isCrossBorder = this.countryOfResidence && this.countryOfResidence !== 'nigeria' && this.businessLocation === 'nigeria';
    return m("div", {
      className: "VouchaCalculator-wizardStep"
    }, m("h2", null, "Business Profile"), m("div", {
      className: "Form-group"
    }, m("label", null, "Your Country of Tax Residence *"), m("select", {
      value: this.countryOfResidence,
      onchange: e => this.countryOfResidence = e.target.value
    }, m("option", {
      value: ""
    }, "Select your country of residence"), m("option", {
      value: "nigeria"
    }, "Nigeria"), m("option", {
      value: "canada"
    }, "Canada"), m("option", {
      value: "us"
    }, "United States"), m("option", {
      value: "uk"
    }, "United Kingdom"), m("option", {
      value: "uae"
    }, "UAE"), m("option", {
      value: "other"
    }, "Other")), m("div", {
      className: "Form-hint"
    }, "Where you live and file personal taxes (tax residency)")), m("div", {
      className: "Form-group"
    }, m("label", null, "Business Operating Location *"), m("select", {
      value: this.businessLocation,
      onchange: e => this.businessLocation = e.target.value
    }, m("option", {
      value: ""
    }, "Select business location"), m("option", {
      value: "nigeria"
    }, "Nigeria"), m("option", {
      value: "other"
    }, "Other")), m("div", {
      className: "Form-hint"
    }, "Where your business is registered and operates")), m("div", {
      className: "Form-group"
    }, m("label", null, "Primary Source of Business Capital *"), m("select", {
      value: this.fundingSource,
      onchange: e => this.fundingSource = e.target.value
    }, m("option", {
      value: ""
    }, "Select funding source"), m("option", {
      value: "local"
    }, "Local Nigerian Funding"), m("option", {
      value: "foreign-personal"
    }, "Foreign Personal Funds"), m("option", {
      value: "foreign-investors"
    }, "Foreign Investor Capital"), m("option", {
      value: "foreign-loans"
    }, "Foreign Bank Loans"), m("option", {
      value: "mixed"
    }, "Mixed Sources")), m("div", {
      className: "Form-hint"
    }, "Where your business capital comes from")), m("div", {
      className: "Form-group"
    }, m("label", null, "Do you repatriate income to your country of residence? *"), m("select", {
      value: this.repatriateIncome,
      onchange: e => this.repatriateIncome = e.target.value
    }, m("option", {
      value: ""
    }, "Select option"), m("option", {
      value: "no-nigeria-resident"
    }, "N/A \u2014 I live in Nigeria"), m("option", {
      value: "no-reinvest"
    }, "No \u2014 Reinvest profits in Nigeria"), m("option", {
      value: "yes-regular"
    }, "Yes \u2014 Regularly"), m("option", {
      value: "yes-occasional"
    }, "Yes \u2014 Occasionally"))), m("div", {
      className: "Form-group"
    }, m("label", null, "Entity Type *"), m("select", {
      value: this.entityType,
      onchange: e => this.entityType = e.target.value
    }, m("option", {
      value: ""
    }, "Select entity type"), m("option", {
      value: "sole-proprietor"
    }, "Sole Proprietor"), m("option", {
      value: "small-company"
    }, "Small Company (\u2264\u20A6100M turnover)"), m("option", {
      value: "large-company"
    }, "Large Company (>\u20A6100M turnover)"))), m("div", {
      className: "Form-group"
    }, m("label", null, "Industry (optional)"), m("input", {
      type: "text",
      value: this.industry,
      oninput: e => this.industry = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Years in Operation"), m("input", {
      type: "number",
      value: this.yearsInOperation,
      oninput: e => this.yearsInOperation = e.target.value
    })), this.countryOfResidence && this.countryOfResidence !== 'nigeria' && TAX_WARNINGS[this.countryOfResidence] && m("div", {
      className: "VouchaCalculator-warningBox"
    }, m("h4", null, TAX_WARNINGS[this.countryOfResidence].title), m.trust(TAX_WARNINGS[this.countryOfResidence].html)), isCrossBorder && m("div", {
      className: "VouchaCalculator-warningBox"
    }, m("h4", null, "\u26A0\uFE0F Cross-Border Business Structure Alert"), m("p", null, "You live outside Nigeria but operate a business inside Nigeria. This creates Permanent Establishment risk, transfer pricing obligations, and WHT on payments sent abroad (10% on dividends, interest, and royalties). Seek cross-border tax advice before operating.")), this.countryOfResidence && this.countryOfResidence !== 'nigeria' && FUNDING_WARNINGS[this.fundingSource] && m("div", {
      className: "VouchaCalculator-warningBox"
    }, m.trust(FUNDING_WARNINGS[this.fundingSource])), this.error && m("div", {
      className: "VouchaCalculator-error"
    }, this.error), m("div", {
      className: "VouchaCalculator-wizardNav"
    }, Button.component({
      className: 'Button Button--primary',
      onclick: () => this.goToStep(2)
    }, 'Next: Income & Revenue')));
  }
  renderStep2() {
    return m("div", {
      className: "VouchaCalculator-wizardStep"
    }, m("h2", null, "Income & Revenue"), m("div", {
      className: "Form-group"
    }, m("label", null, "Gross Revenue *"), m("input", {
      type: "number",
      value: this.grossRevenue,
      oninput: e => this.grossRevenue = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Cost of Sales"), m("input", {
      type: "number",
      value: this.costOfSales,
      oninput: e => this.costOfSales = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Rental Income"), m("input", {
      type: "number",
      value: this.rentalIncome,
      oninput: e => this.rentalIncome = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Dividend Income"), m("input", {
      type: "number",
      value: this.dividendIncome,
      oninput: e => this.dividendIncome = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Interest Income"), m("input", {
      type: "number",
      value: this.interestIncome,
      oninput: e => this.interestIncome = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Capital Gains"), m("input", {
      type: "number",
      value: this.capitalGains,
      oninput: e => this.capitalGains = e.target.value
    })), this.error && m("div", {
      className: "VouchaCalculator-error"
    }, this.error), m("div", {
      className: "VouchaCalculator-wizardNav"
    }, Button.component({
      className: 'Button',
      onclick: () => this.goToStep(1)
    }, 'Back'), Button.component({
      className: 'Button Button--primary',
      onclick: () => this.goToStep(3)
    }, 'Next: Expenses & Deductions')));
  }
  renderStep3() {
    return m("div", {
      className: "VouchaCalculator-wizardStep"
    }, m("h2", null, "Expenses & Deductions"), m("div", {
      className: "Form-group"
    }, m("label", null, "Operating Expenses"), m("input", {
      type: "number",
      value: this.operatingExpenses,
      oninput: e => this.operatingExpenses = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Depreciation"), m("input", {
      type: "number",
      value: this.depreciation,
      oninput: e => this.depreciation = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Interest Expense"), m("input", {
      type: "number",
      value: this.interestExpense,
      oninput: e => this.interestExpense = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Bad Debts"), m("input", {
      type: "number",
      value: this.badDebts,
      oninput: e => this.badDebts = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Pension Contributions"), m("input", {
      type: "number",
      value: this.pensionContributions,
      oninput: e => this.pensionContributions = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Donations"), m("input", {
      type: "number",
      value: this.donations,
      oninput: e => this.donations = e.target.value
    })), this.error && m("div", {
      className: "VouchaCalculator-error"
    }, this.error), m("div", {
      className: "VouchaCalculator-wizardNav"
    }, Button.component({
      className: 'Button',
      onclick: () => this.goToStep(2)
    }, 'Back'), Button.component({
      className: 'Button Button--primary',
      onclick: () => this.calculate()
    }, 'Calculate')));
  }
  renderResults() {
    const r = this.results;
    return m("div", {
      className: "VouchaCalculator-wizardStep"
    }, m("h2", null, "Results \u2014 ", r.entityDescription), brandLine('business-tax', RESIDENCE_COUNTRY[this.countryOfResidence], 'Business tax results'), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Gross Revenue"), m("span", null, formatCurrency(r.grossRevenue))), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Assessable Profit"), m("span", null, formatCurrency(r.assessableProfit))), r.entityType === 'sole-proprietor' ? m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Personal Income Tax (Progressive PAYE)"), m("span", null, formatCurrency(r.citAmount))) : m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Company Income Tax (", (r.citRate * 100).toFixed(0), "%)"), m("span", null, formatCurrency(r.citAmount))), r.developmentLevyAmount > 0 && m("div", {
      className: "VouchaCalculator-resultRow VouchaCalculator-resultRow--sub"
    }, m("span", null, "Development Levy (4%)"), m("span", null, formatCurrency(r.developmentLevyAmount))), r.totalWHT > 0 && m("div", {
      className: "VouchaCalculator-resultRow VouchaCalculator-resultRow--sub"
    }, m("span", null, "Withholding Tax"), m("span", null, formatCurrency(r.totalWHT))), r.cgtAmount > 0 && m("div", {
      className: "VouchaCalculator-resultRow VouchaCalculator-resultRow--sub"
    }, m("span", null, "Capital Gains Tax"), m("span", null, formatCurrency(r.cgtAmount))), m("div", {
      className: "VouchaCalculator-resultRow VouchaCalculator-resultRow--net"
    }, m("span", null, "Total Tax Liability"), m("span", null, formatCurrency(r.totalTax))), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Effective Tax Rate"), m("span", null, r.effectiveTaxRate.toFixed(2), "%")), r.entityType === 'small-company' && m("div", {
      className: "VouchaCalculator-tip"
    }, "Your company qualifies for 0% CIT exemption and no Development Levy. Keep turnover below \u20A6100M and total assets below \u20A6250M to maintain this benefit."), r.entityType === 'sole-proprietor' && r.assessableProfit <= 800000 && m("div", {
      className: "VouchaCalculator-tip"
    }, "Your income is below \u20A6800,000, so you pay 0% tax under the Nigeria Tax Act 2025."), this.countryOfResidence !== 'nigeria' && m("div", {
      className: "VouchaCalculator-warningBox"
    }, m("h4", null, "\u26A0\uFE0F This shows Nigerian taxes only"), m("p", null, "As a tax resident of ", this.countryOfResidence.toUpperCase(), ", you must also report this income and likely pay additional taxes at home (potentially offset by foreign tax credits). Consult a cross-border tax specialist for your total liability.")), app.session.user ? m("div", {
      className: "VouchaCalculator-saveArea"
    }, Button.component({
      className: 'Button',
      onclick: () => this.save(),
      loading: this.saving
    }, 'Save to my account'), this.saveMessage && m("div", {
      className: `VouchaCalculator-saveMessage VouchaCalculator-saveMessage--${this.saveMessage.type}`
    }, this.saveMessage.text)) : m("div", {
      className: "VouchaCalculator-loginPrompt"
    }, m("span", null, "Log in to save this calculation to your account."), Button.component({
      className: 'Button Button--primary',
      onclick: () => {
        const headerLoginBtn = document.querySelector('.item-logIn .Button');
        if (headerLoginBtn) {
          headerLoginBtn.click();
        } else {
          m.route.set('/');
        }
      }
    }, 'Log In')), m("div", {
      className: "VouchaCalculator-wizardNav"
    }, Button.component({
      className: 'Button',
      onclick: () => this.reset()
    }, 'Start Over')));
  }
  renderHistorySection() {
    return m("div", {
      className: "VouchaCalculator-history"
    }, m("div", {
      className: "VouchaCalculator-historyHeader"
    }, m("h2", null, "My Saved Calculations"), m("div", {
      className: "VouchaCalculator-historyActions"
    }, Button.component({
      className: 'Button Button--link',
      onclick: () => this.loadHistory()
    }, 'Refresh'), this.history && this.history.length > 0 && Button.component({
      className: 'Button Button--link VouchaCalculator-clearLink',
      onclick: () => this.clearHistory()
    }, 'Clear'))), this.loadingHistory && LoadingIndicator.component(), this.history && this.history.length === 0 && m("p", {
      className: "helpText"
    }, "No saved calculations yet."), this.history && this.history.length > 0 && m("table", {
      className: "VouchaCalculator-historyTable"
    }, m("thead", null, m("tr", null, m("th", null, "Entity Type"), m("th", null, "Gross Revenue"), m("th", null, "Total Tax"), m("th", null, "Effective Rate"))), m("tbody", null, this.history.map(row => m("tr", null, m("td", null, row.entity_type), m("td", null, formatCurrency(parseFloat(row.gross_revenue) || 0)), m("td", null, formatCurrency(parseFloat(row.total_tax) || 0)), m("td", null, (parseFloat(row.effective_tax_rate) || 0).toFixed(2), "%"))))));
  }
}
flarum.reg.add('voucha-calculators', 'forum/components/BusinessTaxCalculatorPage', BusinessTaxCalculatorPage);